import React from "react";
import * as Yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import { Formik, Form, Field, ErrorMessage, type FormikHelpers } from "formik";
import { Button, Stack, TextField } from "@mui/material";
import {
  DatePicker,
  TimePicker,
  DateTimePicker,
} from "@mui/x-date-pickers-pro";

type FormValues = {
  name: string;
  eventDate: Dayjs | null;
  eventTime: Dayjs | null;
  eventDateTime: Dayjs | null;
};

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  eventDate: Yup.date().required("Date is required"),
  eventTime: Yup.date().required("Time is required"),
  eventDateTime: Yup.date().required("Date and Time are required"),
});

const DatePickerFormik: React.FC = () => {
  const initialValues: FormValues = {
    name: "",
    eventDate: dayjs(),
    eventTime: dayjs(),
    eventDateTime: dayjs(),
  };

  const onSubmit = (
    values: FormValues,
    { setSubmitting, resetForm }: FormikHelpers<FormValues>,
  ) => {
    setTimeout(() => {
      console.log("TEST", values);
      setSubmitting(false);
      resetForm();
    }, 3000);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, touched, errors, setFieldValue, values }) => (
        <Form>
          <Stack spacing={2}>
            {/* Input */}
            <Field
              as={TextField}
              name="name"
              label="Name"
              fullWidth
              error={touched.name && Boolean(errors.name)}
              helperText={<ErrorMessage name="name" />}
            />

            {/* Date Picker */}
            <DatePicker
              label="Event Date"
              value={values.eventDate}
              onChange={(date: Dayjs | null) =>
                setFieldValue("eventDate", date)
              }
              slotProps={{ actionBar: { actions: ["clear"] } }}
              slots={{
                textField: (params) => (
                  <TextField
                    {...params}
                    name="eventDate"
                    fullWidth
                    error={touched.eventDate && Boolean(errors.eventDate)}
                    helperText={<ErrorMessage name="eventDate" />}
                  />
                ),
              }}
            />

            {/* Time Picker */}
            <TimePicker
              label="Select Time"
              value={values.eventTime}
              onChange={(newValue) => setFieldValue("eventTime", newValue)}
              slotProps={{ actionBar: { actions: ["clear"] } }}
              slots={{
                textField: (params) => (
                  <TextField
                    {...params}
                    name="eventTime"
                    fullWidth
                    error={touched.eventTime && Boolean(errors.eventTime)}
                    helperText={<ErrorMessage name="eventTime" />}
                  />
                ),
              }}
            />

            {/* DateTime Picker */}
            <DateTimePicker
              label="Select Date & Time"
              value={values.eventDateTime}
              onChange={(newValue) => setFieldValue("eventDateTime", newValue)}
              slotProps={{ actionBar: { actions: ["clear"] } }}
              slots={{
                textField: (params) => (
                  <TextField
                    {...params}
                    name="eventDateTime"
                    fullWidth
                    error={
                      touched.eventDateTime && Boolean(errors.eventDateTime)
                    }
                    helperText={<ErrorMessage name="eventDateTime" />}
                  />
                ),
              }}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={isSubmitting}
              sx={{ mt: 2 }}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default DatePickerFormik;
